import { useState, useEffect } from 'react';
import * as analyticsApi from '../../api/analytics';
import {
  INITIAL_WIZARD_STATE, buildForecastConfig, configToWizardState,
  addWindowToSet, validateName, STEP_VALIDATORS, deriveStepFromServerError,
  VALID_AGGREGATIONS, firstBlockingViewStep,
} from './forecastWizardHelpers';

const STEP_LABELS = ['Type', 'Prediction', 'Event Sources', 'Feature Windows', 'Custom Features', 'Training', 'EDA'];

// ─── Style helpers ─────────────────────────────────────────────────────────────

const S = {
  label: { display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: '#374151', marginBottom: '4px' },
  input: { width: '100%', border: '1px solid #d1d5db', borderRadius: '6px', padding: '6px 10px', fontSize: '0.875rem', boxSizing: 'border-box' },
  select: { width: '100%', border: '1px solid #d1d5db', borderRadius: '6px', padding: '6px 10px', fontSize: '0.875rem', background: '#fff', boxSizing: 'border-box' },
  field: { marginBottom: '14px' },
  hint: { fontSize: '0.75rem', color: '#6b7280', marginTop: '3px' },
  warn: { fontSize: '0.75rem', color: '#b45309', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '4px', padding: '4px 8px', marginTop: '3px' },
  errBox: { fontSize: '0.75rem', color: '#991b1b', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '4px', padding: '4px 8px', marginTop: '3px' },
  infoBox: { fontSize: '0.8125rem', color: '#1d4ed8', background: '#dbeafe', border: '1px solid #93c5fd', borderRadius: '6px', padding: '8px 12px', marginBottom: '14px' },
  sectionTitle: { fontSize: '0.875rem', fontWeight: 700, color: '#111827', marginBottom: '10px', marginTop: '18px' },
  row: { display: 'flex', gap: '12px' },
  col: { flex: 1 },
};

// ─── WizardProgress ────────────────────────────────────────────────────────────

function WizardProgress({ currentStep }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '20px' }}>
      {STEP_LABELS.map((label, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
          {i > 0 && (
            <div style={{
              position: 'absolute', top: '11px', right: '50%', left: '-50%',
              height: '2px', background: i <= currentStep ? '#2563eb' : '#e5e7eb',
            }} />
          )}
          <div style={{
            width: '22px', height: '22px', borderRadius: '50%', zIndex: 1,
            background: i < currentStep ? '#059669' : i === currentStep ? '#2563eb' : '#e5e7eb',
            color: i <= currentStep ? '#fff' : '#9ca3af',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.6875rem', fontWeight: 700,
          }}>
            {i < currentStep ? '✓' : i + 1}
          </div>
          <span style={{ fontSize: '0.625rem', color: i === currentStep ? '#2563eb' : '#9ca3af', marginTop: '4px', textAlign: 'center', lineHeight: 1.2 }}>
            {label}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── ViewPicker ────────────────────────────────────────────────────────────────
// Shows a <select> of syncable views; warns about unsynced views.
// unsyncedViews: views that pass static checks but have unsynced table deps.
// When editing, the currently-selected view is always present in the option list
// even if it is now unsynced (prevents the <select> rendering blank).

function ViewPicker({ value, views, unsyncedViews = [], viewValidation, loadingValidation, onChange, label, required, testId }) {
  const vv = viewValidation;

  // Post-selection validation status element.
  let statusEl = null;
  if (loadingValidation) {
    statusEl = <div style={S.hint}>Checking view columns…</div>;
  } else if (vv?.permissionDenied) {
    statusEl = <div style={S.errBox}>analytics:write permission required — contact your admin.</div>;
  } else if (vv?.serverError) {
    statusEl = <div style={S.warn}>{vv.error || 'Column discovery failed — enter column names manually.'}</div>;
  } else if (vv?.valid === false && vv?.error_code === 'not_found') {
    statusEl = <div style={S.errBox}>View no longer exists — select a replacement.</div>;
  } else if (vv?.valid === false) {
    statusEl = <div style={S.errBox}>{vv.error || 'View validation failed — select a different view.'}</div>;
  } else if (vv?.valid === true) {
    statusEl = <div style={S.hint}>✓ {vv.columns?.length ?? 0} columns loaded{vv.has_topic ? ' · has topic' : ''}.</div>;
  }

  // If the currently-selected value is an unsynced view, include it in the option
  // list so the <select> never renders blank in edit mode.
  const selectedUnsynced = value ? unsyncedViews.find(v => v.id === value) : null;
  const selectOptions = selectedUnsynced ? [...views, selectedUnsynced] : views;

  return (
    <div style={S.field}>
      <label style={S.label}>{label}{required && <span style={{ color: '#dc2626' }}> *</span>}</label>
      <select
        data-testid={testId}
        style={S.select}
        value={value}
        onChange={e => onChange(e.target.value)}
      >
        <option value="">— select a view —</option>
        {selectOptions.map(v => (
          <option key={v.id} value={v.id}>{v.name}</option>
        ))}
      </select>
      {selectedUnsynced && (
        <div style={S.errBox} data-testid={testId && `${testId}-unsynced-error`}>
          &ldquo;{selectedUnsynced.name}&rdquo; requires these objects to be synced:{' '}
          {(selectedUnsynced.unsynced_objects || [])
            .map(o => `${o.object_label} (${o.data_source_name})`).join(', ')}.
        </div>
      )}
      {!selectedUnsynced && unsyncedViews.length > 0 && (
        <div style={S.warn} data-testid={testId && `${testId}-unsynced-panel`}>
          <strong>Needs sync before use:</strong>{' '}
          {unsyncedViews.map(v =>
            `${v.name}: ${(v.unsynced_objects || []).map(o => o.object_label).join(', ')}`
          ).join('; ')}
        </div>
      )}
      {statusEl}
    </div>
  );
}

// ─── ColumnPicker ──────────────────────────────────────────────────────────────
// <select> when columns are available; <input> when in manual-entry mode.

function ColumnPicker({ value, viewValidation, loadingValidation, label, required, optional, onChange, 'data-testid': testId }) {
  const vv = viewValidation;
  const manualMode = vv?.serverError;
  const columnsAvailable = vv?.valid === true && Array.isArray(vv.columns) && vv.columns.length > 0;

  return (
    <div style={S.field}>
      <label style={S.label}>{label}{required && <span style={{ color: '#dc2626' }}> *</span>}{optional && <span style={{ color: '#9ca3af' }}> (optional)</span>}</label>
      {loadingValidation ? (
        <select style={{ ...S.select, opacity: 0.5 }} disabled><option>Loading…</option></select>
      ) : manualMode ? (
        <input
          data-testid={testId}
          type="text"
          style={S.input}
          value={value}
          placeholder="Type column name"
          onChange={e => onChange(e.target.value)}
        />
      ) : columnsAvailable ? (
        <select
          data-testid={testId}
          style={S.select}
          value={value}
          onChange={e => onChange(e.target.value)}
        >
          <option value="">— select column —</option>
          {vv.columns.map(col => (
            <option key={col} value={col}>{col}</option>
          ))}
        </select>
      ) : (
        <select data-testid={testId} style={{ ...S.select, opacity: 0.5 }} disabled>
          <option>{vv ? 'View invalid — fix above first' : '— select view first —'}</option>
        </select>
      )}
    </div>
  );
}

// ─── Step 0: Forecast Type ─────────────────────────────────────────────────────

const FORECAST_TYPE_OPTIONS = [
  { value: 'binary', label: 'Binary Classification', description: 'Predict a binary outcome (e.g. won vs lost). Most common.' },
  { value: 'stage_progression', label: 'Stage Progression', description: 'Predict movement from one pipeline stage to another.' },
  { value: 'time_to_close', label: 'Time to Close', description: 'Predict a numeric value such as days to close (regression).' },
  { value: 'multi_class', label: 'Multi-Class', description: 'Predict one of several categorical outcomes.' },
  { value: 'survival', label: 'Time-to-Event (Survival)', description: 'Predict when an event will occur (e.g., customer churn, contract renewal). Uses Cox proportional hazards.' },
];

function StepForecastType({ state, onPatch }) {
  return (
    <div>
      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '16px' }}>
        Select the forecast type. This controls the label expression, LightGBM objective, and available metrics.
      </p>
      {FORECAST_TYPE_OPTIONS.map(opt => (
        <label key={opt.value} data-testid={`forecast-type-${opt.value}`}
          style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px', borderRadius: '8px', border: `2px solid ${state.forecast_type === opt.value ? '#2563eb' : '#e5e7eb'}`, background: state.forecast_type === opt.value ? '#eff6ff' : '#fff', cursor: 'pointer', marginBottom: '10px' }}>
          <input type="radio" name="forecast_type" value={opt.value}
            checked={state.forecast_type === opt.value}
            onChange={() => onPatch({ forecast_type: opt.value })}
            style={{ marginTop: '2px', accentColor: '#2563eb' }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#111827' }}>{opt.label}</div>
            <div style={{ fontSize: '0.8125rem', color: '#6b7280', marginTop: '2px' }}>{opt.description}</div>
          </div>
        </label>
      ))}
    </div>
  );
}

// ─── Step 1: Prediction ────────────────────────────────────────────────────────

function StepPrediction({ state, views, unsyncedViews = [], viewValidations, viewValidationsLoading, loadViewValidation, onPatch }) {
  const p = state.prediction;
  const ft = state.forecast_type || 'binary';
  const vv = viewValidations[p.view];
  const loading = viewValidationsLoading.has(p.view);

  function set(field, value) {
    onPatch({ prediction: { ...p, [field]: value } });
  }

  function handleViewChange(viewId) {
    set('view', viewId);
    loadViewValidation(viewId);
  }

  return (
    <div>
      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '16px' }}>
        Select the SOQL view that represents your prediction target (e.g. opportunities with win/loss labels).
      </p>

      <ViewPicker
        label="Prediction view"
        required
        testId="prediction-view-select"
        value={p.view}
        views={views}
        unsyncedViews={unsyncedViews}
        viewValidation={vv}
        loadingValidation={loading}
        onChange={handleViewChange}
      />

      <div style={S.row}>
        <div style={S.col}>
          <ColumnPicker label="Account column" required data-testid="prediction-account-col" value={p.account_col} viewValidation={vv} loadingValidation={loading} onChange={v => set('account_col', v)} />
        </div>
        <div style={S.col}>
          <ColumnPicker label="Opened At column" required data-testid="prediction-opened-at-col" value={p.opened_at_col} viewValidation={vv} loadingValidation={loading} onChange={v => set('opened_at_col', v)} />
        </div>
      </div>

      <div style={S.row}>
        <div style={S.col}>
          <ColumnPicker label="Closed At column" required data-testid="prediction-closed-at-col" value={p.closed_at_col} viewValidation={vv} loadingValidation={loading} onChange={v => set('closed_at_col', v)} />
        </div>
        {(ft === 'binary' || ft === 'stage_progression') && (
          <div style={S.col}>
            <ColumnPicker label="Label column" required data-testid="prediction-label-col" value={p.label_col} viewValidation={vv} loadingValidation={loading} onChange={v => set('label_col', v)} />
          </div>
        )}
      </div>

      {(ft === 'binary' || ft === 'stage_progression') && (
        <div style={S.field}>
          <label style={S.label}>Label value <span style={{ color: '#dc2626' }}>*</span></label>
          <input type="text" style={S.input} value={p.label_value} placeholder='e.g. "true" or "Won"'
            onChange={e => set('label_value', e.target.value)} />
          <div style={S.hint}>The value of the label column that indicates a positive outcome.</div>
        </div>
      )}

      {ft === 'survival' && (
        <>
          <div style={S.row}>
            <div style={S.col}>
              <ColumnPicker label="Duration column (time to event)" required data-testid="duration-col-select"
                value={state.survival?.duration_col ?? ''} viewValidation={vv} loadingValidation={loading}
                onChange={v => onPatch({ survival: { ...state.survival, duration_col: v } })} />
              <div style={S.hint}>Which column represents time-to-event duration? (numeric)</div>
            </div>
            <div style={S.col}>
              <ColumnPicker label="Event indicator column (1=occurred, 0=censored)" required data-testid="event-col-select"
                value={state.survival?.event_col ?? ''} viewValidation={vv} loadingValidation={loading}
                onChange={v => onPatch({ survival: { ...state.survival, event_col: v } })} />
              <div style={S.hint}>Which column indicates the event occurred? (0=censored, 1=event)</div>
            </div>
          </div>
          <div style={S.field}>
            <ColumnPicker label="Stratification column" optional data-testid="strata-col-select"
              value={state.survival?.strata_col ?? ''} viewValidation={vv} loadingValidation={loading}
              onChange={v => onPatch({ survival: { ...state.survival, strata_col: v } })} />
            <div style={S.hint}>Stratify by (optional) — categorical column for subgroup analysis.</div>
          </div>
        </>
      )}

      {ft === 'stage_progression' && (
        <div style={S.row}>
          <div style={S.col}>
            <div style={S.field}>
              <label style={S.label}>From stage <span style={{ color: '#9ca3af' }}>(optional)</span></label>
              <input type="text" style={S.input} value={state.stage_progression?.from_stage ?? ''}
                onChange={e => onPatch({ stage_progression: { ...state.stage_progression, from_stage: e.target.value } })} />
            </div>
          </div>
          <div style={S.col}>
            <div style={S.field}>
              <label style={S.label}>To stage <span style={{ color: '#9ca3af' }}>(optional)</span></label>
              <input type="text" style={S.input} value={state.stage_progression?.to_stage ?? ''}
                onChange={e => onPatch({ stage_progression: { ...state.stage_progression, to_stage: e.target.value } })} />
            </div>
          </div>
        </div>
      )}

      {ft === 'time_to_close' && (
        <div style={S.field}>
          <label style={S.label}>Target column <span style={{ color: '#dc2626' }}>*</span></label>
          <ColumnPicker label="" required value={state.regression?.target_col ?? ''} viewValidation={vv} loadingValidation={loading}
            onChange={v => onPatch({ regression: { ...state.regression, target_col: v } })} />
          <div style={S.hint}>Numeric column to predict (e.g. days to close). Leave blank to auto-compute from opened/closed dates.</div>
        </div>
      )}

      {ft === 'multi_class' && (
        <>
          <div style={S.field}>
            <label style={S.label}>Class column <span style={{ color: '#dc2626' }}>*</span></label>
            <ColumnPicker label="" required value={state.multi_class?.class_col ?? ''} viewValidation={vv} loadingValidation={loading}
              onChange={v => onPatch({ multi_class: { ...state.multi_class, class_col: v } })} />
            <div style={S.hint}>Column holding the categorical outcome value.</div>
          </div>
          <div style={S.field}>
            <label style={S.label}>Classes (comma-separated) <span style={{ color: '#dc2626' }}>*</span></label>
            <input type="text" style={S.input}
              value={(state.multi_class?.classes ?? []).join(', ')}
              placeholder='e.g. "Won, Lost, Disqualified"'
              onChange={e => {
                const classes = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                onPatch({ multi_class: { ...state.multi_class, classes } });
              }} />
            <div style={S.hint}>Ordered list of possible class values. Index order determines LightGBM label encoding.</div>
          </div>
        </>
      )}

      <div style={S.field}>
        <label style={S.label}>Horizon (days) <span style={{ color: '#dc2626' }}>*</span></label>
        <input data-testid="prediction-horizon-input" type="number" min="1" style={{ ...S.input, maxWidth: '140px' }} value={p.horizon_days}
          onChange={e => set('horizon_days', e.target.value)} />
        <div style={S.hint}>How many days into the future to predict.</div>
      </div>

      <div style={S.field}>
        <label style={S.label}>Record ID column <span style={{ color: '#9ca3af' }}>(optional)</span></label>
        <ColumnPicker label="" required={false} optional value={p.record_id_col} viewValidation={vv} loadingValidation={loading} onChange={v => set('record_id_col', v)} />
        {!p.record_id_col && (
          <div style={S.warn}>Omitting this disables per-record scoring after promotion. Set it to the row's unique ID column (e.g. <code>Id</code>).</div>
        )}
      </div>
    </div>
  );
}

// ─── Step 1: Event Sources ─────────────────────────────────────────────────────

function StepEventSources({ state, views, unsyncedViews = [], viewValidations, viewValidationsLoading, loadViewValidation, onPatch }) {
  function addSource() {
    onPatch({
      event_sources: [...state.event_sources, {
        view: '', account_col: '', occurred_at_col: '',
        event_type_col: '', event_type_literal: '',
        direction_col: '', result_col: '', result_type: 'boolean', topic_col: '',
      }],
    });
  }

  function removeSource(i) {
    onPatch({ event_sources: state.event_sources.filter((_, idx) => idx !== i) });
  }

  function setSource(i, field, value) {
    const updated = state.event_sources.map((es, idx) =>
      idx === i ? { ...es, [field]: value } : es
    );
    onPatch({ event_sources: updated });
  }

  function handleViewChange(i, viewId) {
    setSource(i, 'view', viewId);
    loadViewValidation(viewId);
  }

  return (
    <div>
      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '16px' }}>
        Add one or more event streams (e.g. email activity, calls, meetings) to generate features from.
      </p>

      {state.event_sources.map((es, i) => {
        const vv = viewValidations[es.view];
        const loading = viewValidationsLoading.has(es.view);
        return (
          <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '14px', marginBottom: '12px', background: '#f9fafb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <span style={{ fontWeight: 600, fontSize: '0.875rem', color: '#111827' }}>Event Source {i + 1}</span>
              <button type="button" onClick={() => removeSource(i)}
                style={{ fontSize: '0.75rem', color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px' }}>
                Remove
              </button>
            </div>

            <ViewPicker label="Event view" required testId={`evtsrc-${i}-view-select`} value={es.view} views={views}
              unsyncedViews={unsyncedViews} viewValidation={vv} loadingValidation={loading}
              onChange={v => handleViewChange(i, v)} />

            <div style={S.row}>
              <div style={S.col}>
                <ColumnPicker label="Account column" required data-testid={`evtsrc-${i}-account-col`} value={es.account_col} viewValidation={vv} loadingValidation={loading} onChange={v => setSource(i, 'account_col', v)} />
              </div>
              <div style={S.col}>
                <ColumnPicker label="Occurred At column" required data-testid={`evtsrc-${i}-occurred-at-col`} value={es.occurred_at_col} viewValidation={vv} loadingValidation={loading} onChange={v => setSource(i, 'occurred_at_col', v)} />
              </div>
            </div>

            <div style={S.row}>
              <div style={S.col}>
                <ColumnPicker label="Event type column" optional value={es.event_type_col} viewValidation={vv} loadingValidation={loading} onChange={v => setSource(i, 'event_type_col', v)} />
              </div>
              <div style={S.col}>
                <div style={S.field}>
                  <label style={S.label}>Event type literal <span style={{ color: '#9ca3af' }}>(optional)</span></label>
                  <input type="text" style={S.input} value={es.event_type_literal} placeholder='e.g. "email"'
                    onChange={e => setSource(i, 'event_type_literal', e.target.value)} />
                </div>
              </div>
            </div>

            <div style={S.row}>
              <div style={S.col}>
                <ColumnPicker label="Direction column" optional value={es.direction_col} viewValidation={vv} loadingValidation={loading} onChange={v => setSource(i, 'direction_col', v)} />
              </div>
              <div style={S.col}>
                <ColumnPicker label="Result column" optional value={es.result_col} viewValidation={vv} loadingValidation={loading} onChange={v => setSource(i, 'result_col', v)} />
              </div>
            </div>

            {es.result_col && (
              <div style={S.field}>
                <label style={S.label}>Result type</label>
                <select style={{ ...S.select, maxWidth: '160px' }} value={es.result_type}
                  onChange={e => setSource(i, 'result_type', e.target.value)}>
                  <option value="boolean">boolean</option>
                  <option value="numeric">numeric</option>
                </select>
              </div>
            )}

            {vv?.has_topic && (
              <ColumnPicker label="Topic column" optional value={es.topic_col} viewValidation={vv} loadingValidation={loading} onChange={v => setSource(i, 'topic_col', v)} />
            )}
          </div>
        );
      })}

      <button type="button" onClick={addSource}
        style={{ fontSize: '0.8125rem', color: '#2563eb', background: 'none', border: '1px dashed #93c5fd', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', width: '100%' }}>
        + Add event source
      </button>
    </div>
  );
}

// ─── Step 2: Feature Windows & Aggregations ────────────────────────────────────

function StepFeatureGeneration({ state, onPatch }) {
  const [windowInput, setWindowInput] = useState('');

  function addWindow() {
    const next = addWindowToSet(state.windows, windowInput);
    if (next !== state.windows) {
      onPatch({ windows: next });
    }
    setWindowInput('');
  }

  function removeWindow(w) {
    onPatch({ windows: state.windows.filter(x => x !== w) });
  }

  function toggleAgg(agg) {
    const next = state.aggregations.includes(agg)
      ? state.aggregations.filter(a => a !== agg)
      : [...state.aggregations, agg];
    onPatch({ aggregations: next });
  }


  return (
    <div>
      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '16px' }}>
        Define the time windows and aggregation functions used to build features from event sources.
      </p>

      <div style={S.sectionTitle}>Time windows (days)</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
        {state.windows.map(w => (
          <span key={w} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#dbeafe', color: '#1d4ed8', borderRadius: '9999px', padding: '2px 10px', fontSize: '0.8125rem', fontWeight: 600 }}>
            {w}d
            <button type="button" onClick={() => removeWindow(w)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6', lineHeight: 1, padding: 0 }}>×</button>
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <input
          data-testid="window-custom-input"
          type="number" min="1" style={{ ...S.input, maxWidth: '120px' }}
          value={windowInput} placeholder="e.g. 60"
          onChange={e => setWindowInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addWindow(); } }}
        />
        <button type="button" onClick={addWindow}
          style={{ padding: '6px 14px', background: '#2563eb', color: '#fff', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '0.8125rem' }}>
          Add
        </button>
      </div>
      <div style={S.hint}>Must be positive integers. Duplicates and invalid entries are ignored.</div>

      <div style={S.sectionTitle}>Aggregations</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
        {VALID_AGGREGATIONS.map(agg => (
          <label key={agg} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.875rem', color: '#374151' }}>
            <input type="checkbox"
              checked={state.aggregations.includes(agg)}
              onChange={() => toggleAgg(agg)}
            />
            {agg}
          </label>
        ))}
      </div>
    </div>
  );
}

// ─── Step 3: Custom Features ───────────────────────────────────────────────────

function StepCustomFeatures({ state, onPatch }) {
  function addFeature() {
    onPatch({
      user_features: [...state.user_features, {
        name: '', window: state.windows[0] ?? 30,
        aggregation: state.aggregations[0] ?? 'count',
        event_source_index: 0,
        filter: { event_type: '', topic_id: '', topic_config_name: '', direction: '', result_eq_ui: 'any' },
      }],
    });
  }

  function removeFeature(i) {
    onPatch({ user_features: state.user_features.filter((_, idx) => idx !== i) });
  }

  function setFeature(i, field, value) {
    onPatch({
      user_features: state.user_features.map((uf, idx) =>
        idx === i ? { ...uf, [field]: value } : uf
      ),
    });
  }

  function setFilter(i, field, value) {
    onPatch({
      user_features: state.user_features.map((uf, idx) =>
        idx === i ? { ...uf, filter: { ...uf.filter, [field]: value } } : uf
      ),
    });
  }

  return (
    <div>
      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '8px' }}>
        Optionally define custom named features with specific filters. Leave empty to use only auto-generated features.
      </p>

      {state.user_features.length === 0 && (
        <div style={S.infoBox}>No custom features — the model will use auto-generated features from all event sources.</div>
      )}

      {state.user_features.map((uf, i) => (
        <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '14px', marginBottom: '12px', background: '#f9fafb' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontWeight: 600, fontSize: '0.875rem', color: '#111827' }}>Custom Feature {i + 1}</span>
            <button type="button" onClick={() => removeFeature(i)}
              style={{ fontSize: '0.75rem', color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>
              Remove
            </button>
          </div>

          <div style={S.row}>
            <div style={S.col}>
              <div style={S.field}>
                <label style={S.label}>Feature name <span style={{ color: '#dc2626' }}>*</span></label>
                <input type="text" style={S.input} value={uf.name} placeholder="e.g. recent_calls"
                  onChange={e => setFeature(i, 'name', e.target.value)} />
              </div>
            </div>
            <div style={S.col}>
              <div style={S.field}>
                <label style={S.label}>Event source</label>
                <select style={S.select} value={uf.event_source_index}
                  onChange={e => setFeature(i, 'event_source_index', Number(e.target.value))}>
                  {state.event_sources.map((es, idx) => (
                    <option key={idx} value={idx}>Source {idx + 1}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div style={S.row}>
            <div style={S.col}>
              <div style={S.field}>
                <label style={S.label}>Window</label>
                <select style={S.select} value={uf.window}
                  onChange={e => setFeature(i, 'window', Number(e.target.value))}>
                  {state.windows.map(w => <option key={w} value={w}>{w} days</option>)}
                </select>
              </div>
            </div>
            <div style={S.col}>
              <div style={S.field}>
                <label style={S.label}>Aggregation</label>
                <select style={S.select} value={uf.aggregation}
                  onChange={e => setFeature(i, 'aggregation', e.target.value)}>
                  {state.aggregations.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div style={{ ...S.sectionTitle, marginTop: '8px' }}>Filters</div>
          <div style={S.row}>
            <div style={S.col}>
              <div style={S.field}>
                <label style={S.label}>Event type filter</label>
                <input type="text" style={S.input} value={uf.filter.event_type} placeholder="e.g. email"
                  onChange={e => setFilter(i, 'event_type', e.target.value)} />
              </div>
            </div>
            <div style={S.col}>
              <div style={S.field}>
                <label style={S.label}>Direction filter</label>
                <input type="text" style={S.input} value={uf.filter.direction} placeholder='e.g. "outbound"'
                  onChange={e => setFilter(i, 'direction', e.target.value)} />
              </div>
            </div>
          </div>

          <div style={S.row}>
            <div style={S.col}>
              <div style={S.field}>
                <label style={S.label}>Topic ID filter</label>
                <input type="text" style={S.input} value={uf.filter.topic_id}
                  onChange={e => setFilter(i, 'topic_id', e.target.value)} />
              </div>
            </div>
            <div style={S.col}>
              <div style={S.field}>
                <label style={S.label}>Topic config name {uf.filter.topic_id && <span style={{ color: '#dc2626' }}>*</span>}</label>
                <input type="text" style={S.input} value={uf.filter.topic_config_name}
                  onChange={e => setFilter(i, 'topic_config_name', e.target.value)} />
              </div>
            </div>
          </div>

          <div style={S.field}>
            <label style={S.label}>Result equality</label>
            <div style={{ display: 'flex', gap: '16px' }}>
              {['any', 'true', 'false'].map(opt => (
                <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.875rem' }}>
                  <input type="radio" name={`result_eq_${i}`} value={opt}
                    checked={uf.filter.result_eq_ui === opt}
                    onChange={() => setFilter(i, 'result_eq_ui', opt)} />
                  {opt}
                </label>
              ))}
            </div>
          </div>
        </div>
      ))}

      <button type="button" onClick={addFeature}
        style={{ fontSize: '0.8125rem', color: '#2563eb', background: 'none', border: '1px dashed #93c5fd', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', width: '100%' }}>
        + Add custom feature
      </button>
    </div>
  );
}

// ─── Step 4: Training ──────────────────────────────────────────────────────────

function StepTraining({ state, onPatch }) {
  const t = state.training;

  function set(field, value) {
    onPatch({ training: { ...t, [field]: value } });
  }

  return (
    <div>
      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '16px' }}>
        Configure how LightGBM is trained. Defaults are suitable for most datasets.
      </p>

      <div style={S.field}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
          <input type="checkbox" checked={t.auto_split} onChange={e => set('auto_split', e.target.checked)} />
          Auto split (use the last 20% of data for testing)
        </label>
        <div style={S.hint}>Uncheck to specify an exact date as the train/test boundary.</div>
      </div>

      {!t.auto_split && (
        <div style={S.field}>
          <label style={S.label}>Split date <span style={{ color: '#dc2626' }}>*</span></label>
          <input type="date" style={{ ...S.input, maxWidth: '200px' }} value={t.split_date}
            onChange={e => set('split_date', e.target.value)} />
          <div style={S.hint}>Records created before this date are used for training; on or after for testing.</div>
        </div>
      )}

      <div style={{ ...S.sectionTitle, marginTop: '20px' }}>LightGBM hyperparameters</div>

      <div style={S.row}>
        <div style={S.col}>
          <div style={S.field}>
            <label style={S.label}>Num leaves</label>
            <input type="number" min="2" style={S.input} value={t.num_leaves}
              onChange={e => set('num_leaves', e.target.value)} />
            <div style={S.hint}>Maximum tree leaves. Higher = more complex model. Default: 31.</div>
          </div>
        </div>
        <div style={S.col}>
          <div style={S.field}>
            <label style={S.label}>Num iterations</label>
            <input type="number" min="1" style={S.input} value={t.num_iterations}
              onChange={e => set('num_iterations', e.target.value)} />
            <div style={S.hint}>Boosting rounds. Default: 100.</div>
          </div>
        </div>
      </div>

      <div style={S.row}>
        <div style={S.col}>
          <div style={S.field}>
            <label style={S.label}>Learning rate</label>
            <input type="number" min="0.001" max="1" step="0.01" style={S.input} value={t.learning_rate}
              onChange={e => set('learning_rate', e.target.value)} />
            <div style={S.hint}>Shrinkage rate. Default: 0.1.</div>
          </div>
        </div>
        <div style={S.col}>
          <div style={S.field}>
            <label style={S.label}>Min data in leaf</label>
            <input type="number" min="1" style={S.input} value={t.min_data_in_leaf}
              onChange={e => set('min_data_in_leaf', e.target.value)} />
            <div style={S.hint}>Minimum samples per leaf. Default: 20.</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Step 5: EDA ───────────────────────────────────────────────────────────────

function StepEDA({ state, onPatch }) {
  const e = state.eda;

  function set(field, value) {
    onPatch({ eda: { ...e, [field]: value } });
  }

  return (
    <div>
      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '16px' }}>
        Tune the exploratory data analysis thresholds. These control which features are flagged or dropped before training.
      </p>

      <div style={S.row}>
        <div style={S.col}>
          <div style={S.field}>
            <label style={S.label}>Correlation threshold</label>
            <input type="number" min="0" max="1" step="0.01" style={S.input} value={e.correlation_threshold}
              onChange={ev => set('correlation_threshold', ev.target.value)} />
            <div style={S.hint}>Features correlated above this are flagged as redundant. Default: 0.95.</div>
          </div>
        </div>
        <div style={S.col}>
          <div style={S.field}>
            <label style={S.label}>Weak signal threshold</label>
            <input type="number" min="0" max="1" step="0.001" style={S.input} value={e.weak_signal_threshold}
              onChange={ev => set('weak_signal_threshold', ev.target.value)} />
            <div style={S.hint}>Features with variance below this are dropped. Default: 0.02.</div>
          </div>
        </div>
      </div>

      <div style={S.row}>
        <div style={S.col}>
          <div style={S.field}>
            <label style={S.label}>Sparse event threshold</label>
            <input type="number" min="1" style={S.input} value={e.sparse_event_threshold}
              onChange={ev => set('sparse_event_threshold', ev.target.value)} />
            <div style={S.hint}>Accounts with fewer events than this are flagged. Default: 5.</div>
          </div>
        </div>
        <div style={S.col}>
          <div style={S.field}>
            <label style={S.label}>Imbalance threshold</label>
            <input type="number" min="0" max="1" step="0.01" style={S.input} value={e.imbalance_threshold}
              onChange={ev => set('imbalance_threshold', ev.target.value)} />
            <div style={S.hint}>Minimum positive class ratio before imbalance warning. Default: 0.1.</div>
          </div>
        </div>
      </div>

      <div style={S.row}>
        <div style={S.col}>
          <div style={S.field}>
            <label style={S.label}>Pairplot top N</label>
            <input type="number" min="1" style={S.input} value={e.pairplot_top_n}
              onChange={ev => set('pairplot_top_n', ev.target.value)} />
            <div style={S.hint}>Features included in the pairplot visualization. Default: 15.</div>
          </div>
        </div>
        <div style={S.col}>
          <div style={S.field}>
            <label style={S.label}>UMAP components</label>
            <input type="number" min="2" max="10" style={S.input} value={e.umap_components}
              onChange={ev => set('umap_components', ev.target.value)} />
            <div style={S.hint}>Dimensions for UMAP projection. Default: 2.</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main wizard ───────────────────────────────────────────────────────────────

export default function ForecastConfigWizard({
  title,
  initialName = '',
  initialDescription = '',
  initialConfig = null,
  submitLabel = 'Save',
  onSubmit,
  onCancel,
}) {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [nameError, setNameError] = useState(null);

  const [wizardState, setWizardState] = useState(() => configToWizardState(initialConfig));
  // initialCanonical: null in create mode (config always sent); JSON string in edit mode.
  // Dirty detection at submit time via round-trip equality: buildForecastConfig ∘ configToWizardState.
  const [initialCanonical] = useState(() =>
    initialConfig
      ? JSON.stringify(buildForecastConfig(configToWizardState(initialConfig)))
      : null
  );

  const [currentStep, setCurrentStep] = useState(0);
  const [stepErrors, setStepErrors] = useState([]);
  const [globalError, setGlobalError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // viewValidations: { [viewId]: ValidateForecastViewResponse & { permissionDenied?, serverError? } }
  const [viewValidations, setViewValidations] = useState({});
  const [viewValidationsLoading, setViewValidationsLoading] = useState(new Set());

  const [views, setViews] = useState([]);
  const [degradedSync, setDegradedSync] = useState(false);
  const [viewLoadError, setViewLoadError] = useState(false);

  // Load views on mount using the forecast-specific prefilter endpoint.
  // This endpoint returns only statically-compatible views, annotated with sync status.
  // On failure: show explicit error — do NOT fall back to fetchViews (loses sync signal).
  useEffect(() => {
    analyticsApi.fetchForecastViewPrefilter()
      .then(data => {
        setViews(Array.isArray(data) ? data : (data?.views || []));
        if (data?.degraded) setDegradedSync(true);
      })
      .catch(() => setViewLoadError(true));
  }, []);

  // Edit-mode preload: call validate-view for all views already in the config
  useEffect(() => {
    if (!initialConfig) return;
    const viewIds = new Set();
    if (initialConfig.prediction?.view) viewIds.add(initialConfig.prediction.view);
    (initialConfig.event_sources || []).forEach(es => { if (es.view) viewIds.add(es.view); });
    viewIds.forEach(id => loadViewValidation(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally runs only on mount

  async function loadViewValidation(viewId) {
    if (!viewId || viewValidations[viewId]) return;
    setViewValidationsLoading(prev => new Set([...prev, viewId]));
    try {
      const res = await analyticsApi.validateForecastView(viewId);
      const data = res?.data ?? res;
      setViewValidations(prev => ({ ...prev, [viewId]: data }));
    } catch (err) {
      const status = err.status;
      if (status === 403) {
        // Fatal — same analytics:write scope required for save; wizard cannot proceed
        setGlobalError('You do not have analytics:write permission — contact your admin.');
        setViewValidations(prev => ({ ...prev, [viewId]: { valid: false, permissionDenied: true,
          error: 'analytics:write permission required.' } }));
      } else if (status === 404) {
        setViewValidations(prev => ({
          ...prev,
          [viewId]: { valid: false, error_code: 'not_found',
                      error: 'View no longer exists — select a replacement.' },
        }));
      } else if (status === 400) {
        // Structural validation failure — not transient, manual entry would fail on save too
        setViewValidations(prev => ({
          ...prev,
          [viewId]: { valid: false, error: err.data?.message || 'View failed validation.' },
        }));
      } else {
        // 5xx or network error — transient, allow manual column entry
        setViewValidations(prev => ({
          ...prev,
          [viewId]: { valid: false, serverError: true,
                      error: err.data?.message || err.message || 'Column discovery failed.' },
        }));
      }
    } finally {
      setViewValidationsLoading(prev => { const s = new Set(prev); s.delete(viewId); return s; });
    }
  }

  function handlePatch(patch) {
    setWizardState(prev => ({ ...prev, ...patch }));
  }

  function handleNext() {
    const nameErrs = validateName(name);
    if (nameErrs.length) { setNameError(nameErrs[0]); return; }
    setNameError(null);
    const blockingStep = firstBlockingViewStep(wizardState, viewValidations);
    if (blockingStep === currentStep) {
      setStepErrors(['Fix the view error above before continuing.']);
      return;
    }
    const errs = STEP_VALIDATORS[currentStep](wizardState);
    if (errs.length) { setStepErrors(errs); return; }
    setStepErrors([]);
    setGlobalError(null);
    setCurrentStep(s => s + 1);
  }

  function handleBack() {
    setStepErrors([]);
    setCurrentStep(s => s - 1);
  }

  async function handleSubmit() {
    const nameErrs = validateName(name);
    if (nameErrs.length) { setNameError(nameErrs[0]); return; }
    setNameError(null);
    const blockingStep = firstBlockingViewStep(wizardState, viewValidations);
    if (blockingStep !== null) {
      setCurrentStep(blockingStep);
      setStepErrors(['Fix the view error before saving.']);
      return;
    }
    const errs = STEP_VALIDATORS[currentStep](wizardState);
    if (errs.length) { setStepErrors(errs); return; }
    setStepErrors([]);
    setGlobalError(null);
    setSubmitting(true);
    try {
      const currentCanonical = JSON.stringify(buildForecastConfig(wizardState));
      const payload = { name: name.trim(), description: description.trim() };
      if (!initialCanonical || currentCanonical !== initialCanonical) {
        payload.config = buildForecastConfig(wizardState);
      }
      await onSubmit(name.trim(), description.trim(), payload);
    } catch (err) {
      const errData = err.data || {};
      const step = deriveStepFromServerError(errData);
      const msg = formatServerError(errData, err);
      if (step === 'global') {
        setGlobalError(msg);
      } else {
        setCurrentStep(step);
        setStepErrors([msg]);
      }
    } finally {
      setSubmitting(false);
    }
  }

  function formatServerError(errData, err) {
    const code = errData?.error;
    if (code === 'config_locked') return 'Config is assembling or training — wait before editing.';
    if (code === 'duckdb_not_available') return 'Column validation service unavailable — try again shortly.';
    if (code === 'not_found') return 'Config no longer exists — refresh the page.';
    const fields = errData?.fields;
    if (fields) {
      const msgs = Object.entries(fields).map(([k, v]) => `${k}: ${v}`).join('; ');
      return `Validation error — ${msgs}`;
    }
    return errData?.message || err?.message || 'Save failed.';
  }

  // Derive syncable vs. unsynced view lists from the prefilter response.
  // syncableViews: views with sync_ok=true (or sync_ok not set — null treated as ok).
  // unsyncedViews: views with sync_ok===false.
  const syncableViews = views.filter(v => v.sync_ok !== false);
  const unsyncedViews = views.filter(v => v.sync_ok === false);

  const stepProps = { state: wizardState, views: syncableViews, unsyncedViews, viewValidations, viewValidationsLoading, loadViewValidation, onPatch: handlePatch };

  const stepComponents = [
    <StepForecastType key="type" state={wizardState} onPatch={handlePatch} />,
    <StepPrediction key="pred" {...stepProps} />,
    <StepEventSources key="evt" {...stepProps} />,
    <StepFeatureGeneration key="feat" {...stepProps} />,
    <StepCustomFeatures key="custom" {...stepProps} />,
    <StepTraining key="train" {...stepProps} />,
    <StepEDA key="eda" {...stepProps} />,
  ];

  const isLast = currentStep === STEP_LABELS.length - 1;

  return (
    // Modal overlay
    <div
      data-testid="forecast-wizard-modal"
      style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(17,24,39,0.5)', padding: '16px' }}
    >
      {/* Card */}
      <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', width: '100%', maxWidth: '680px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ margin: 0, fontSize: '1.0625rem', fontWeight: 700, color: '#111827' }}>{title}</h2>
            <button type="button" onClick={onCancel}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '1.25rem', lineHeight: 1, padding: '2px 6px' }}>
              ✕
            </button>
          </div>

          <WizardProgress currentStep={currentStep} />

          {/* Name + description (always visible) */}
          <div style={S.row}>
            <div style={{ flex: 2 }}>
              <label style={S.label}>Name <span style={{ color: '#dc2626' }}>*</span></label>
              <input
                data-testid="forecast-wizard-name-input"
                type="text" style={S.input} value={name} placeholder="e.g. Q4 Win-Rate Forecast"
                onChange={e => { setName(e.target.value); if (nameError) setNameError(null); }}
              />
              {nameError && <div style={S.errBox}>{nameError}</div>}
            </div>
            <div style={{ flex: 3 }}>
              <label style={S.label}>Description</label>
              <input type="text" style={S.input} value={description} placeholder="Optional"
                onChange={e => setDescription(e.target.value)} />
            </div>
          </div>
        </div>

        {/* Step body */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
          {/* View load error: shown when the prefilter endpoint failed entirely */}
          {viewLoadError && (
            <div style={{ ...S.errBox, marginBottom: '14px' }} data-testid="view-load-error">
              Could not load views — please refresh and try again.
            </div>
          )}
          {/* Degraded sync banner: shown when sync status is temporarily unavailable */}
          {degradedSync && (
            <div style={{ ...S.warn, marginBottom: '14px' }} data-testid="degraded-sync-banner">
              Sync status is temporarily unavailable — verify views before saving.
            </div>
          )}
          {/* Cross-step view error banner: shown when a preloaded view is invalid but we're not on its step */}
          {initialConfig && (() => {
            const bs = firstBlockingViewStep(wizardState, viewValidations);
            if (bs === null || bs === currentStep) return null;
            const viewIds = [wizardState.prediction?.view, ...(wizardState.event_sources || []).map(e => e.view)];
            for (const vid of viewIds) {
              const vv = viewValidations[vid];
              if (vv && !vv.valid && !vv.serverError) {
                return (
                  <div style={{ ...S.errBox, marginBottom: '14px' }}>
                    {vv.error || 'A saved view is invalid — go to the Prediction step to fix.'}
                  </div>
                );
              }
            }
            return null;
          })()}
          {stepComponents[currentStep]}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid #e5e7eb', flexShrink: 0 }}>
          {stepErrors.length > 0 && (
            <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: '6px', padding: '8px 12px', marginBottom: '10px', fontSize: '0.8125rem' }}>
              {stepErrors.map((e, i) => <div key={i}>{e}</div>)}
            </div>
          )}
          {globalError && (
            <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: '6px', padding: '8px 12px', marginBottom: '10px', fontSize: '0.8125rem' }}>
              {globalError}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button type="button" onClick={handleBack} disabled={currentStep === 0}
              style={{ padding: '8px 18px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', color: currentStep === 0 ? '#9ca3af' : '#374151', cursor: currentStep === 0 ? 'default' : 'pointer', fontSize: '0.875rem' }}>
              Back
            </button>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" onClick={onCancel}
                style={{ padding: '8px 18px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', color: '#374151', cursor: 'pointer', fontSize: '0.875rem' }}>
                Cancel
              </button>
              {!isLast ? (
                <button type="button" onClick={handleNext}
                  style={{ padding: '8px 20px', background: '#2563eb', color: '#fff', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600 }}>
                  Next →
                </button>
              ) : (
                <button type="button" onClick={handleSubmit} disabled={submitting}
                  data-testid="forecast-wizard-submit-btn"
                  style={{ padding: '8px 20px', background: submitting ? '#93c5fd' : '#2563eb', color: '#fff', borderRadius: '6px', border: 'none', cursor: submitting ? 'default' : 'pointer', fontSize: '0.875rem', fontWeight: 600 }}>
                  {submitting ? 'Saving…' : submitLabel}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
